import { Maybe, Price, PriceList, StockStatus } from '../../types';
import { PriceDisplay, PriceDisplayEnum, SortOptionEnum } from './types';

enum SortDirection {
  Ascending,
  Descending,
}

interface IPropertyResolver<T> {
  resolver: (offer: Price) => T;
  sortDirection: SortDirection;
}

const sortByPriceProp = (priceDisplay: PriceDisplay) => {
  const resolver =
    priceDisplay === PriceDisplayEnum.PRICE_INCLUDES_SHIPPING
      ? (offer: Price) => {
          return offer?.price?.inclShipping || offer?.price?.exclShipping;
        }
      : (offer: Price) => offer.price && offer.price.exclShipping;

  return { resolver, sortDirection: SortDirection.Ascending };
};

const sortByShippingPriceProp = () => {
  const resolver = (offer: Price) =>
    (offer.price && offer.price.inclShipping) || Infinity;
  return { resolver, sortDirection: SortDirection.Ascending };
};

const sortByStockStatusProp = () => {
  const resolver = (offer: Price) =>
    offer.stock?.status === 'in_stock' ? 0 : 1;
  return { resolver, sortDirection: SortDirection.Ascending };
};

const sortByUserReviewProp = () => {
  const resolver = (offer: Price) =>
    offer.store?.userReviewSummary?.rating || 0;
  return { resolver, sortDirection: SortDirection.Descending };
};

const sortByUserReviewCountProp = () => {
  const resolver = (offer: Price) =>
    offer.store?.userReviewSummary?.countTotal || 0;
  return { resolver, sortDirection: SortDirection.Descending };
};

export const sortByPrice = (priceDisplay: PriceDisplay, prices: Price[]) => {
  const priceSort = sortByPriceProp(priceDisplay);
  const shippingPriceProp = sortByShippingPriceProp();
  const stockStatusProp = sortByStockStatusProp();
  const userReviewProp = sortByUserReviewProp();
  const userReviewCountProp = sortByUserReviewCountProp();
  return genericSort(
    prices,
    priceSort,
    shippingPriceProp,
    stockStatusProp,
    userReviewProp,
    userReviewCountProp
  );
};

export const sortByWeight = (prices: Price[]) => {
  return genericSort(prices, {
    sortDirection: SortDirection.Descending,
    resolver: offer => {
      return offer?.weight || 0;
    },
  });
};

const getStockStatusAsNumber = (
  stockStatus: StockStatus | undefined | null,
  featured: boolean | undefined | null
) => {
  if (!featured) {
    return 0;
  }

  switch (stockStatus) {
    case 'in_stock':
      return 5;
    case 'in_stock_preorder':
      return 4;
    case 'incoming':
      return 3;
    case 'not_in_stock':
      return 1;
    case 'unknown':
    default:
      return 0;
  }
};

export const sortByStockStatus = (
  priceDisplay: PriceDisplay,
  prices: Price[]
) => {
  const primary = {
    resolver: (offer: Price) =>
      getStockStatusAsNumber(offer?.stock?.status, offer?.store?.featured),
    sortDirection: SortDirection.Descending,
  };
  const secondary = sortByPriceProp(priceDisplay);
  return genericSort(prices, primary, secondary);
};

export const sortByFeatured = (priceDisplay: PriceDisplay, prices: Price[]) => {
  const primary = {
    resolver: (offer: Price) => Number(offer?.store?.featured),
    sortDirection: SortDirection.Descending,
  };
  const secondary = sortByPriceProp(priceDisplay);
  return genericSort(prices, primary, secondary);
};

const genericSort = <T, TU>(
  prices: Price[],
  primaryProp: IPropertyResolver<T>,
  secondaryProp?: IPropertyResolver<TU>,
  thirdProp?: IPropertyResolver<TU>,
  fouthProp?: IPropertyResolver<TU>,
  fifthProp?: IPropertyResolver<TU>
) => {
  return [...prices].sort((a, b) => {
    const firstSort = orderItem(a, b, primaryProp);
    if (firstSort !== 0) {
      return firstSort;
    }

    if (secondaryProp) {
      const secondSort = orderItem(a, b, secondaryProp);
      if (secondSort !== 0) {
        return secondSort;
      }
    }

    if (thirdProp) {
      const thirdSort = orderItem(a, b, thirdProp);
      if (thirdSort !== 0) {
        return thirdSort;
      }
    }

    if (fouthProp) {
      const fourthSort = orderItem(a, b, fouthProp);
      if (fourthSort !== 0) {
        return fourthSort;
      }
    }

    if (fifthProp) {
      const fifthSort = orderItem(a, b, fifthProp);
      if (fifthSort !== 0) {
        return fifthSort;
      }
    }

    return 0;
  });
};

const orderItem = <T>(a: Price, b: Price, prop: IPropertyResolver<T>) => {
  const aProp = prop.resolver(a);
  const bProp = prop.resolver(b);

  if (aProp < bProp) {
    return prop.sortDirection === SortDirection.Descending ? 1 : -1;
  }

  if (aProp > bProp) {
    return prop.sortDirection === SortDirection.Descending ? -1 : 1;
  }

  return 0;
};

export const getSortedPrices = (
  priceDisplay: PriceDisplay,
  sorting: SortOptionEnum,
  prices: Price[]
): Price[] => {
  if (prices.length) {
    switch (sorting) {
      case SortOptionEnum.PRICE:
        return sortByPrice(priceDisplay, prices);
      case SortOptionEnum.RECOMMENDED:
        return sortByWeight(prices);
      case SortOptionEnum.PRICEANDSTOCKSTATUS:
        return sortByStockStatus(priceDisplay, prices);
      case SortOptionEnum.FEATURED:
        return sortByFeatured(priceDisplay, prices);
      default:
        return prices;
    }
  }
  return [];
};

export const getAllOffersAsArray = (prices?: Maybe<PriceList>): Price[] => {
  const offers = prices?.nodes?.filter(x => x) as Price[] | null | undefined;
  return offers ?? [];
};
